import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Textarea } from "../../../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import { Label } from "../../../ui/label";
import { Link2, Copy, CheckCircle, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import useAuth from "../../../../hooks/useAuth";
import { config } from "../../../../Environment";

interface RecommendModalProps {
  open: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
}

export function RecommendModal({
  open,
  onClose,
  workflowId,
  workflowName,
}: RecommendModalProps) {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { auth, user } = useAuth();
  const [productLink, setProductLink] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Check if user is logged in
  const isLoggedIn = Boolean(auth?.email || user?.email);

  // Email form state
  const [toEmail, setToEmail] = useState("");
  const [fromEmail, setFromEmail] = useState(auth?.email || user?.email || "");
  const [personalMessage, setPersonalMessage] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  useEffect(() => {
    const fetchProductLink = async () => {
      if (!open || !workflowId) return;

      try {
        setIsLoading(true);
        const response = await retryFetch(
          `${config.API_BASE}/api/prescription/${workflowId}/product-link`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Construct the share product URL (for social media previews)
          const shareUrl = `${window.location.origin}/share/product?eid=${data.entityId}`;
          setProductLink(shareUrl);
        } else {
          toast.error(t("product_not_found") || "Product not found in marketplace");
          onClose();
        }
      } catch (error) {
        console.error("Failed to fetch product link:", error);
        toast.error(t("failed_to_load_product_link") || "Failed to load product link");
        onClose();
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workflowId]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(productLink);
      setIsCopied(true);
      toast.success(t("link_copied") || "Link copied to clipboard!");
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error(t("failed_to_copy") || "Failed to copy link");
    }
  };

  const handleSendEmail = async () => {
    // Validation
    if (!toEmail || !toEmail.includes("@")) {
      toast.error(t("invalid_email") || "Please enter a valid email address");
      return;
    }

    if (!fromEmail || !fromEmail.includes("@")) {
      toast.error(t("invalid_from_email") || "Your email address is invalid");
      return;
    }

    try {
      setIsSendingEmail(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/recommendation/send-email`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            toEmail,
            fromEmail,
            workflowName,
            productLink,
            personalMessage,
          }),
        }
      );

      if (response.ok) {
        toast.success(
          t("recommendation_sent") || "Recommendation email sent successfully!"
        );
        // Reset form
        setToEmail("");
        setPersonalMessage("");
        onClose();
      } else {
        const error = await response.json();
        toast.error(
          error.message || t("failed_to_send_email") || "Failed to send email"
        );
      }
    } catch (error) {
      console.error("Failed to send recommendation email:", error);
      toast.error(t("failed_to_send_email") || "Failed to send email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            {t("recommend_activity") || "Recommend Activity"}
          </DialogTitle>
          <DialogDescription>
            {t("recommend_activity_description") ||
              "Share this activity so others can check it out."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              {t("activity_name") || "Activity Name"}
            </label>
            <p className="p-2 text-sm text-gray-900 rounded bg-gray-50">
              {workflowName}
            </p>
          </div>

          {isLoggedIn ? (
            <Tabs defaultValue="link" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="link" className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  {t("copy_link") || "Copy Link"}
                </TabsTrigger>
                <TabsTrigger value="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {t("send_email") || "Send Email"}
                </TabsTrigger>
              </TabsList>

            <TabsContent value="link" className="mt-4 space-y-4">
              <div>
                <Label htmlFor="product-link" className="block mb-1 text-sm font-medium text-gray-700">
                  {t("marketplace_link") || "Marketplace Link"}
                </Label>
                {isLoading ? (
                  <div className="flex items-center justify-center p-3 rounded bg-gray-50">
                    <div className="w-5 h-5 border-b-2 rounded-full animate-spin border-primary"></div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      id="product-link"
                      value={productLink}
                      readOnly
                      className="flex-1"
                      onClick={(e) => e.currentTarget.select()}
                    />
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      size="icon"
                      className="flex-shrink-0"
                    >
                      {isCopied ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                )}
              </div>

              <div className="p-3 border border-primary/20 rounded-lg bg-primary/5">
                <p className="text-sm text-primary">
                  {t("recommend_info") ||
                    "Recipients will be directed to the marketplace where they can learn more about this activity."}
                </p>
              </div>
            </TabsContent>

            <TabsContent value="email" className="mt-4 space-y-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="from-email" className="block mb-1 text-sm font-medium text-gray-700">
                    {t("from_email") || "From (Your Email)"}
                  </Label>
                  <Input
                    id="from-email"
                    type="email"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    placeholder={t("your_email") || "your@email.com"}
                  />
                </div>

                <div>
                  <Label htmlFor="to-email" className="block mb-1 text-sm font-medium text-gray-700">
                    {t("to_email") || "To (Recipient Email)"}
                  </Label>
                  <Input
                    id="to-email"
                    type="email"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    placeholder={t("recipient_email") || "recipient@email.com"}
                  />
                </div>

                <div>
                  <Label htmlFor="personal-message" className="block mb-1 text-sm font-medium text-gray-700">
                    {t("personal_message") || "Personal Message (Optional)"}
                  </Label>
                  <Textarea
                    id="personal-message"
                    value={personalMessage}
                    onChange={(e) => setPersonalMessage(e.target.value)}
                    placeholder={t("personal_message_placeholder") || "Add a personal note..."}
                    rows={4}
                    className="resize-none"
                  />
                </div>

                <div className="p-3 border border-primary/20 rounded-lg bg-primary/5">
                  <p className="text-sm text-primary">
                    {t("email_recommendation_info") ||
                      "An email will be sent with a link to this activity in the marketplace."}
                  </p>
                </div>

                <Button
                  onClick={handleSendEmail}
                  disabled={isSendingEmail || isLoading}
                  className="w-full"
                >
                  {isSendingEmail ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-b-2 border-white rounded-full animate-spin"></div>
                      {t("sending") || "Sending..."}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {t("send_recommendation") || "Send Recommendation"}
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
          ) : (
            // Non-logged-in users: show only copy link option
            <div className="space-y-4">
              <div>
                <Label htmlFor="product-link-public" className="block mb-1 text-sm font-medium text-gray-700">
                  {t("marketplace_link") || "Marketplace Link"}
                </Label>
                {isLoading ? (
                  <div className="flex items-center justify-center p-3 rounded bg-gray-50">
                    <div className="w-5 h-5 border-b-2 rounded-full animate-spin border-primary"></div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      id="product-link-public"
                      value={productLink}
                      readOnly
                      className="flex-1"
                      onClick={(e) => e.currentTarget.select()}
                    />
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      size="icon"
                      className="flex-shrink-0"
                    >
                      {isCopied ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                )}
              </div>

              <div className="p-3 border border-primary/20 rounded-lg bg-primary/5">
                <p className="text-sm text-primary">
                  {t("recommend_info") ||
                    "Recipients will be directed to the marketplace where they can learn more about this activity."}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            {t("close") || "Close"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RecommendModal;
